import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Activity, RefreshCw, TrendingUp, Loader2, AlertTriangle, Trophy, Info,
  Bell, BellOff, Send, PlayCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PRIVILEGED_ROLES = new Set(["super_admin", "sysadmin", "owner"]);
const WINDOW_OPTIONS = [
  { hours: 1,   label: "1h"  },
  { hours: 6,   label: "6h"  },
  { hours: 24,  label: "24h" },
  { hours: 72,  label: "72h" },
  { hours: 168, label: "7d"  },
];

// Same color thresholds as the user-facing chip so the operator's mental
// model stays consistent between the per-user chip and the admin view.
const dotColorFor = (pct) => (pct >= 90 ? "#3ddc97" : pct >= 70 ? "#ffb84a" : "#f87171");

export default function AdminReliability() {
  const { user } = useAuth();
  const [hours, setHours]         = useState(24);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState("");

  const load = useCallback(async (h) => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/admin/karaoke/reliability?hours=${h}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load reliability");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(hours); }, [load, hours]);

  const plat = data?.platform || {};
  const stations = data?.stations || [];

  // Stations at risk: failure_pct >= 20 AND at least 3 terminal renders
  // (avoid singling out a station that had 1 render fail = "100% failure").
  const struggling = useMemo(
    () => stations.filter((s) => s.failed_pct >= 20 && s.terminal >= 3),
    [stations]
  );

  // Route guard: only privileged roles can view this page
  if (!user) return null;
  if (!PRIVILEGED_ROLES.has(user.role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="admin-reliability-page">
      <PageHeader
        eyebrow="// ADMIN · RELIABILITY"
        title="Karaoke pipeline health across every station."
        description="First-try success, auto-retry recoveries, and hard failures — bucketed platform-wide and per station. Spot regressions before customers do."
      />

      {/* ── Window selector + refresh ─────────────────────────────── */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="type-caption text-neutral-500 mr-2">Window:</span>
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.hours}
              type="button"
              data-testid={`admin-reliability-window-${opt.hours}`}
              onClick={() => setHours(opt.hours)}
              className="rounded-full px-3 py-1 type-caption font-medium transition"
              style={{
                background: hours === opt.hours ? "#a78bfa" : "#161923",
                color:      hours === opt.hours ? "#0e0f14" : "#a9b3c9",
                border:     hours === opt.hours ? "1px solid #a78bfa" : "1px solid #262b3a",
                fontSize:   11,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          data-testid="admin-reliability-refresh"
          onClick={() => load(hours)}
          className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 type-caption"
          style={{ background: "#161923", border: "1px solid #262b3a", color: "#a9b3c9", fontSize: 11 }}
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="text-center py-16 text-neutral-500">
          <Loader2 className="mx-auto animate-spin" size={24} />
          <div className="mt-3 type-caption">Aggregating {hours}h of pipeline data…</div>
        </div>
      )}

      {err && (
        <div className="rounded-lg border p-4 mb-6"
             style={{ background: "rgba(248,113,113,0.08)", borderColor: "rgba(248,113,113,0.3)", color: "#f87171" }}
             data-testid="admin-reliability-error">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} />
            <span>{err}</span>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* ── Platform aggregate cards ──────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <PlatformCard
              icon={TrendingUp}
              label="First-try success"
              testid="admin-plat-first-try"
              value={plat.first_try_pct}
              subtitle={`${plat.first_try_count || 0} renders`}
              color={dotColorFor(plat.first_try_pct || 0)}
            />
            <PlatformCard
              icon={RefreshCw}
              label="Recovered via retry"
              testid="admin-plat-recovered"
              value={plat.recovered_pct}
              subtitle={`${plat.recovered_count || 0} renders`}
              color="#ffb84a"
            />
            <PlatformCard
              icon={AlertTriangle}
              label="Failed"
              testid="admin-plat-failed"
              value={plat.failed_pct}
              subtitle={`${plat.failed_count || 0} renders`}
              color="#f87171"
            />
            <PlatformCard
              icon={Activity}
              label="In flight"
              testid="admin-plat-in-flight"
              value={null}
              rawValue={plat.in_flight ?? 0}
              subtitle={`of ${plat.total || 0} total`}
              color="#8ba3d1"
            />
          </div>

          {/* ── Stations at risk (bubble to top if failure > 20%) ─── */}
          {struggling.length > 0 && (
            <div
              className="rounded-lg border p-4 mb-6"
              style={{ background: "rgba(248,113,113,0.06)", borderColor: "rgba(248,113,113,0.25)" }}
              data-testid="admin-reliability-struggling"
            >
              <div className="flex items-center gap-2 mb-2" style={{ color: "#f87171", fontWeight: 600, fontSize: 13 }}>
                <AlertTriangle size={14} />
                {struggling.length} station{struggling.length === 1 ? "" : "s"} above 20% failure rate
              </div>
              <div className="type-caption text-neutral-400" style={{ fontSize: 11 }}>
                These stations are experiencing above-average pipeline failures in the last {hours}h.
                Consider proactive outreach — Whisper / LALAL.AI / storage regressions typically
                surface here first.
              </div>
            </div>
          )}

          {/* ── Per-station table ─────────────────────────────────── */}
          <div className="rounded-xl border overflow-hidden"
               style={{ background: "#1a1e2a", borderColor: "#232632" }}>
            <div className="flex items-center justify-between px-4 py-3"
                 style={{ borderBottom: "1px solid #232632" }}>
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-neutral-400" />
                <span style={{ color: "#e7ebf6", fontSize: 13, fontWeight: 600 }}>
                  Per-station breakdown
                </span>
              </div>
              <span className="type-caption text-neutral-500" style={{ fontSize: 11 }}>
                {data.station_count} station{data.station_count === 1 ? "" : "s"} · sorted by failure count
              </span>
            </div>

            {stations.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <Info className="mx-auto mb-2 opacity-50" size={20} />
                <div className="type-caption">No karaoke renders in the last {hours}h.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full type-caption" style={{ fontSize: 12 }}>
                  <thead style={{ background: "#161923", color: "#7b8397" }}>
                    <tr>
                      <th className="text-left px-4 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>STATION</th>
                      <th className="text-left px-2 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>PLAN</th>
                      <th className="text-right px-2 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>TOTAL</th>
                      <th className="text-right px-2 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>FIRST-TRY</th>
                      <th className="text-right px-2 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>RECOVERED</th>
                      <th className="text-right px-2 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>FAILED</th>
                      <th className="text-right px-4 py-2" style={{ fontWeight: 500, fontSize: 10, letterSpacing: 0.5 }}>IN-FLIGHT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stations.map((s) => (
                      <tr key={s.station_id}
                          data-testid={`admin-station-row-${s.station_id}`}
                          style={{ borderTop: "1px solid #232632" }}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span style={{
                              width: 6, height: 6, borderRadius: 999,
                              background: dotColorFor(s.first_try_pct),
                              boxShadow: `0 0 4px ${dotColorFor(s.first_try_pct)}88`,
                            }} />
                            <span style={{ color: "#e7ebf6" }}>{s.label}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-neutral-400 uppercase" style={{ fontSize: 10 }}>
                          {s.plan || "—"}
                        </td>
                        <td className="px-2 py-2 text-right text-neutral-400">{s.total}</td>
                        <td className="px-2 py-2 text-right" style={{ color: "#3ddc97" }}>
                          {s.first_try_pct}% <span className="text-neutral-600" style={{ fontSize: 10 }}>({s.first_try_count})</span>
                        </td>
                        <td className="px-2 py-2 text-right" style={{ color: "#ffb84a" }}>
                          ↺ {s.recovered_pct}% <span className="text-neutral-600" style={{ fontSize: 10 }}>({s.recovered_count})</span>
                        </td>
                        <td className="px-2 py-2 text-right" style={{ color: s.failed_pct >= 20 ? "#f87171" : "#7b8397" }}>
                          ✕ {s.failed_pct}% <span className="text-neutral-600" style={{ fontSize: 10 }}>({s.failed_count})</span>
                        </td>
                        <td className="px-4 py-2 text-right text-neutral-400">{s.in_flight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-4 type-caption text-neutral-600 flex items-center gap-1.5" style={{ fontSize: 10 }}>
            <Info size={10} />
            Data window: last {hours}h · in-flight rows excluded from percentages · soft-deleted rows excluded
          </div>

          {/* ── Alert settings (Update #159) ────────────────────────── */}
          <AlertSettingsPanel />
        </>
      )}
    </div>
  );
}

function PlatformCard({ icon: Icon, label, value, rawValue, subtitle, color, testid }) {
  const displayValue = value == null ? rawValue : `${value}%`;
  return (
    <div className="rounded-xl border p-4"
         style={{ background: "#1a1e2a", borderColor: "#232632" }}
         data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span className="type-caption text-neutral-400" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase" }}>
          {label}
        </span>
      </div>
      <div style={{ color, fontSize: 24, fontWeight: 600, lineHeight: 1.1 }}>
        {displayValue}
      </div>
      <div className="mt-1 type-caption text-neutral-500" style={{ fontSize: 10 }}>
        {subtitle}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Alert settings panel (Update #159) — configure Slack / Discord /
// email webhook alerts that fire when the platform failed_pct crosses
// a threshold. Collapsed by default to keep the dashboard focused; the
// operator expands it when they need to change thresholds.
// ────────────────────────────────────────────────────────────────────
function AlertSettingsPanel() {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);

  // Local editable form state (mirrors backend shape minus the secret
  // webhook URLs — those write-only, we show a mask instead).
  const [form, setForm] = useState({
    enabled: false,
    threshold_pct: 15,
    min_samples: 5,
    window_hours: 1,
    cooldown_minutes: 60,
    email: "",
    enabled_channels: ["email"],
  });
  const [slackInput, setSlackInput]     = useState("");
  const [discordInput, setDiscordInput] = useState("");

  const loadCfg = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/admin/karaoke/reliability/alert-config"),
        api.get("/admin/karaoke/reliability/alert-history?limit=10"),
      ]);
      setCfg(c.data);
      setForm({
        enabled:          !!c.data.enabled,
        threshold_pct:    c.data.threshold_pct,
        min_samples:      c.data.min_samples,
        window_hours:     c.data.window_hours,
        cooldown_minutes: c.data.cooldown_minutes,
        email:            c.data.email || "",
        enabled_channels: c.data.enabled_channels || ["email"],
      });
      setHistory(h.data.items || []);
    } catch (e) {
      toast.error("Could not load alert config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (expanded) loadCfg(); }, [expanded, loadCfg]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const patch = { ...form };
      // Only send webhook fields if user actually typed new values
      if (slackInput.trim())   patch.slack_webhook   = slackInput.trim();
      if (discordInput.trim()) patch.discord_webhook = discordInput.trim();
      const r = await api.put("/admin/karaoke/reliability/alert-config", patch);
      setCfg(r.data);
      setSlackInput("");
      setDiscordInput("");
      toast.success("Alert config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [form, slackInput, discordInput]);

  const testSend = useCallback(async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/karaoke/reliability/alert-config/test-send");
      const { email_ok, slack_ok, discord_ok, ok } = r.data;
      const parts = [];
      if (email_ok)   parts.push("Email ✓");
      if (slack_ok)   parts.push("Slack ✓");
      if (discord_ok) parts.push("Discord ✓");
      if (ok) toast.success(`Test dispatched — ${parts.join(", ")}`);
      else    toast.error("Test dispatched but no channel accepted the delivery. Check webhook URLs / email address.");
      loadCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  }, [loadCfg]);

  const runSweep = useCallback(async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/karaoke/reliability/alert-sweep");
      const { fired, over_threshold, in_cooldown, failed_pct, terminal } = r.data;
      if (fired === "alert")    toast.success(`Alert fired — failed_pct=${failed_pct}% over ${terminal} renders`);
      else if (fired === "recovery") toast.success(`Recovery notice fired — failed_pct back to ${failed_pct}%`);
      else if (in_cooldown)     toast.info(`In cooldown — would have fired (${failed_pct}% failed)`);
      else if (over_threshold)  toast.info(`Over threshold but alerts disabled (${failed_pct}% failed)`);
      else                      toast.info(`All good — failed_pct=${failed_pct}%`);
      loadCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  }, [loadCfg]);

  const toggleChannel = (ch) => {
    setForm((f) => {
      const has = f.enabled_channels.includes(ch);
      const next = has ? f.enabled_channels.filter((c) => c !== ch) : [...f.enabled_channels, ch];
      return { ...f, enabled_channels: next.length ? next : ["email"] };
    });
  };

  return (
    <div className="mt-8 rounded-xl border overflow-hidden"
         style={{ background: "#1a1e2a", borderColor: "#232632" }}
         data-testid="karaoke-alert-settings">
      {/* Header — click to expand */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="karaoke-alert-settings-toggle"
        className="w-full flex items-center justify-between px-4 py-3 transition"
        style={{ borderBottom: expanded ? "1px solid #232632" : "none", background: "transparent" }}
      >
        <div className="flex items-center gap-2">
          {(cfg?.enabled) ? (
            <Bell size={14} style={{ color: "#3ddc97" }} />
          ) : (
            <BellOff size={14} className="text-neutral-500" />
          )}
          <span style={{ color: "#e7ebf6", fontSize: 13, fontWeight: 600 }}>
            Webhook alerts
          </span>
          {cfg && (
            <span className="type-caption text-neutral-500 ml-2" style={{ fontSize: 11 }}>
              {cfg.enabled
                ? `Firing when failed_pct > ${cfg.threshold_pct}% over ${cfg.window_hours}h`
                : "Disabled"}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} className="text-neutral-400" /> : <ChevronDown size={14} className="text-neutral-400" />}
      </button>

      {expanded && (
        <div className="px-4 py-4">
          {loading && (
            <div className="text-center py-6 text-neutral-500">
              <Loader2 className="mx-auto animate-spin" size={18} />
            </div>
          )}

          {!loading && cfg && (
            <>
              {/* ── Enable toggle ─────────────────────────────────── */}
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="karaoke-alert-enabled-toggle"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="accent-violet-500"
                />
                <span style={{ color: "#e7ebf6", fontSize: 13, fontWeight: 500 }}>
                  Enable platform-wide karaoke alerts
                </span>
              </label>

              {/* ── Threshold + sample / window / cooldown ─────── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <NumberField
                  label="Threshold (failed %)"
                  testid="karaoke-alert-threshold"
                  value={form.threshold_pct}
                  min={1} max={100} step={1}
                  onChange={(v) => setForm({ ...form, threshold_pct: v })}
                />
                <NumberField
                  label="Min sample size"
                  testid="karaoke-alert-min-samples"
                  value={form.min_samples}
                  min={1} max={200} step={1}
                  onChange={(v) => setForm({ ...form, min_samples: v })}
                />
                <NumberField
                  label="Window (hours)"
                  testid="karaoke-alert-window"
                  value={form.window_hours}
                  min={1} max={168} step={1}
                  onChange={(v) => setForm({ ...form, window_hours: v })}
                />
                <NumberField
                  label="Cooldown (minutes)"
                  testid="karaoke-alert-cooldown"
                  value={form.cooldown_minutes}
                  min={5} max={1440} step={5}
                  onChange={(v) => setForm({ ...form, cooldown_minutes: v })}
                />
              </div>

              {/* ── Channels ──────────────────────────────────── */}
              <div className="mb-3 type-caption text-neutral-400" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase" }}>
                Channels
              </div>
              <div className="space-y-3 mb-4">
                <ChannelField
                  channel="email" label="Email"
                  enabled={form.enabled_channels.includes("email")}
                  onToggle={() => toggleChannel("email")}
                  configured={!!form.email}
                >
                  <input
                    type="text"
                    data-testid="karaoke-alert-email-input"
                    placeholder="ops@yourdomain.com  (comma-separated for multiple)"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded px-2 py-1.5"
                    style={{ background: "#0f1119", border: "1px solid #262b3a", color: "#e7ebf6", fontSize: 12 }}
                  />
                </ChannelField>

                <ChannelField
                  channel="slack" label="Slack"
                  enabled={form.enabled_channels.includes("slack")}
                  onToggle={() => toggleChannel("slack")}
                  configured={cfg.slack_configured}
                  mask={cfg.slack_masked}
                >
                  <input
                    type="password"
                    data-testid="karaoke-alert-slack-input"
                    placeholder="https://hooks.slack.com/services/T…"
                    value={slackInput}
                    onChange={(e) => setSlackInput(e.target.value)}
                    className="w-full rounded px-2 py-1.5"
                    style={{ background: "#0f1119", border: "1px solid #262b3a", color: "#e7ebf6", fontSize: 12 }}
                  />
                </ChannelField>

                <ChannelField
                  channel="discord" label="Discord"
                  enabled={form.enabled_channels.includes("discord")}
                  onToggle={() => toggleChannel("discord")}
                  configured={cfg.discord_configured}
                  mask={cfg.discord_masked}
                >
                  <input
                    type="password"
                    data-testid="karaoke-alert-discord-input"
                    placeholder="https://discord.com/api/webhooks/…"
                    value={discordInput}
                    onChange={(e) => setDiscordInput(e.target.value)}
                    className="w-full rounded px-2 py-1.5"
                    style={{ background: "#0f1119", border: "1px solid #262b3a", color: "#e7ebf6", fontSize: 12 }}
                  />
                </ChannelField>
              </div>

              {/* ── Action buttons ─────────────────────────────── */}
              <div className="flex gap-2 flex-wrap mb-6">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="karaoke-alert-save"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: "linear-gradient(135deg,#FF8A1F 0%,#a78bfa 100%)",
                    color: "#fff", fontSize: 12, fontWeight: 500,
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                  Save config
                </button>
                <button
                  type="button"
                  onClick={testSend}
                  disabled={testing}
                  data-testid="karaoke-alert-test-send"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: "#161923", border: "1px solid #262b3a",
                    color: "#a9b3c9", fontSize: 12,
                  }}
                >
                  {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Test send
                </button>
                <button
                  type="button"
                  onClick={runSweep}
                  disabled={sweeping}
                  data-testid="karaoke-alert-run-sweep"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: "#161923", border: "1px solid #262b3a",
                    color: "#a9b3c9", fontSize: 12,
                  }}
                >
                  {sweeping ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                  Run sweep now
                </button>
              </div>

              {/* ── Alert history ─────────────────────────────── */}
              {history.length > 0 && (
                <div className="mt-4">
                  <div className="type-caption text-neutral-400 mb-2" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    Recent alert history
                  </div>
                  <div className="rounded border overflow-hidden" style={{ borderColor: "#232632" }}>
                    <table className="w-full type-caption" style={{ fontSize: 11 }}>
                      <thead style={{ background: "#0f1119", color: "#7b8397" }}>
                        <tr>
                          <th className="text-left px-3 py-1.5" style={{ fontWeight: 500 }}>WHEN</th>
                          <th className="text-left px-3 py-1.5" style={{ fontWeight: 500 }}>KIND</th>
                          <th className="text-right px-3 py-1.5" style={{ fontWeight: 500 }}>FAILED %</th>
                          <th className="text-right px-3 py-1.5" style={{ fontWeight: 500 }}>SAMPLES</th>
                          <th className="text-center px-3 py-1.5" style={{ fontWeight: 500 }}>CHANNELS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h) => {
                          const kindColor =
                            h.kind === "alert"    ? "#f87171"
                          : h.kind === "recovery" ? "#3ddc97"
                          : "#a9b3c9";
                          return (
                            <tr key={h.alert_id} data-testid={`karaoke-alert-history-${h.alert_id}`}
                                style={{ borderTop: "1px solid #232632" }}>
                              <td className="px-3 py-1.5 text-neutral-400">{(h.created_at || "").slice(0, 19).replace("T", " ")}</td>
                              <td className="px-3 py-1.5" style={{ color: kindColor, fontWeight: 500 }}>{h.kind}</td>
                              <td className="px-3 py-1.5 text-right text-neutral-400">{h.failed_pct == null ? "—" : `${h.failed_pct}%`}</td>
                              <td className="px-3 py-1.5 text-right text-neutral-400">{h.terminal_count ?? "—"}</td>
                              <td className="px-3 py-1.5 text-center text-neutral-500" style={{ fontSize: 10 }}>
                                {h.email_ok && "✉ "}
                                {h.slack_ok && "S "}
                                {h.discord_ok && "D"}
                                {!h.email_ok && !h.slack_ok && !h.discord_ok && "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NumberField({ label, value, min, max, step, onChange, testid }) {
  return (
    <label className="block">
      <div className="type-caption text-neutral-400 mb-1" style={{ fontSize: 10, letterSpacing: 0.3, textTransform: "uppercase" }}>
        {label}
      </div>
      <input
        type="number"
        data-testid={testid}
        value={value}
        min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded px-2 py-1.5"
        style={{ background: "#0f1119", border: "1px solid #262b3a", color: "#e7ebf6", fontSize: 12 }}
      />
    </label>
  );
}

function ChannelField({ channel, label, enabled, onToggle, configured, mask, children }) {
  return (
    <div className="rounded p-3" style={{ background: "#161923", border: "1px solid #232632" }}>
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox"
          data-testid={`karaoke-alert-channel-${channel}`}
          checked={enabled}
          onChange={onToggle}
          className="accent-violet-500"
        />
        <span style={{ color: "#e7ebf6", fontSize: 12, fontWeight: 500 }}>{label}</span>
        {configured && (
          <span className="text-neutral-500" style={{ fontSize: 10 }}>· {mask || "configured"}</span>
        )}
      </label>
      {children}
    </div>
  );
}
